/**
 * Provides a single shared Socket.IO connection to the app, plus a helper
 * hook to subscribe to progress events for a specific jobId.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { io } from 'socket.io-client';
import { API_URL } from '../api/api';

const SocketContext = createContext(null);

export function SocketProvider({ children }) {
  const socket = useMemo(() => io(API_URL, { autoConnect: true }), []);

  useEffect(() => {
    return () => socket.disconnect();
  }, [socket]);

  return <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>;
}

export function useSocket() {
  return useContext(SocketContext);
}

/**
 * Subscribe to `progress` events for a given jobId.
 * @param {string} jobId
 * @param {Function} onProgress - (payload) => void
 */
export function useJobProgress(jobId, onProgress) {
  const socket = useSocket();

  useEffect(() => {
    if (!socket || !jobId) return undefined;

    socket.emit('join', jobId);
    const handler = (payload) => {
      if (payload.jobId === jobId || !payload.jobId) onProgress(payload);
    };
    socket.on('progress', handler);

    return () => socket.off('progress', handler);
  }, [socket, jobId, onProgress]);
}
